import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody } from '../../../common/models/common';
import { IAzureSavingsPlanOrders } from '../../../services/azure/azureSavingsPlanOrders/azureSavingsPlanOrders.model';
import { RootState } from '../../app.model';
import {
  searchAzureSavingsPlanOrders,
  getAzureSavingsPlanOrdersById,
  saveAzureSavingsPlanOrders,
  deleteAzureSavingsPlanOrders,
} from './azureSavingsPlanOrders.action';
import { IAzureSavingsPlanOrdersState } from './azureSavingsPlanOrders.model';

export const initialState: IAzureSavingsPlanOrdersState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const azureSavingsPlanOrdersSlice = createSlice({
  name: 'azureSavingsPlanOrders',
  initialState,
  reducers: {
    clearAzureSavingsPlanOrders: () => {
      return initialState;
    },
    clearAzureSavingsPlanOrdersMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearAzureSavingsPlanOrdersGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchAzureSavingsPlanOrders.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchAzureSavingsPlanOrders.fulfilled.type]: (state, action: PayloadAction<any>) => {
      const { records, total_count, table_name, column_selection } = action.payload;
      state.search.data = records;
      state.search.count = total_count;
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = table_name;
      if (column_selection) {
        state.tableColumnSelection.id = column_selection.id;
        const tableSelectionObj = JSON.parse(column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = table_name;
    },
    [searchAzureSavingsPlanOrders.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getAzureSavingsPlanOrdersById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getAzureSavingsPlanOrdersById.fulfilled.type]: (
      state,
      action: PayloadAction<IAzureSavingsPlanOrders>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getAzureSavingsPlanOrdersById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveAzureSavingsPlanOrders.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveAzureSavingsPlanOrders.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveAzureSavingsPlanOrders.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteAzureSavingsPlanOrders.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteAzureSavingsPlanOrders.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteAzureSavingsPlanOrders.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const azureSavingsPlanOrdersSelector = (state: RootState) => state.azureSavingsPlanOrders;

// Actions
export const {
  clearAzureSavingsPlanOrders,
  clearAzureSavingsPlanOrdersMessages,
  clearAzureSavingsPlanOrdersGetById,
  setTableColumnSelection,
} = azureSavingsPlanOrdersSlice.actions;

// The reducer
export default azureSavingsPlanOrdersSlice.reducer;
